// EssayThemesView — admin gerencia a Biblioteca de Temas de Redação.
// [[project-essay-themes-library]]
//
// Lista + modal de criar/editar tema com:
// - Metadados (banca, year, phase, edition, applicationDate, examEditionKey, etc)
// - Statement, officialInstructions, directives (markdown)
// - Fragments + items aninhados (kind text/image/table, source, sourceKind)
//
// Editor é textarea com hint de markdown — não tem preview side-by-side ainda
// (pode evoluir). Suporta colar JSON pré-formatado (botão "Importar JSON").

import { signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import {
  AlertCircle,
  Eye,
  EyeOff,
  FilePlus2,
  ImageIcon,
  Loader2,
  Pencil,
  PenSquare,
  Plus,
  RefreshCw,
  Save,
  Table2,
  Trash2,
  Upload,
  X
} from "lucide-react";
import { Skeleton } from "@app/ui/components/skeleton";
import {
  api,
  ApiError,
  type EssayThemeRow,
  type EssayThemeFull,
  type EssayThemeUpsertInput,
  type EssayThemeFragment,
  type EssayThemeItem,
  type LibraryFolderRow
} from "./api";
import { openAlert, openConfirm } from "./ConfirmPromptModal";
import { friendlyError } from "@app/ui/lib/friendly-error";

interface ListState {
  phase: "idle" | "loading" | "ready" | "error";
  themes: EssayThemeRow[];
  errMsg: string | null;
}

const listState = signal<ListState>({ phase: "idle", themes: [], errMsg: null });
const composer = signal<EssayThemeFull | null>(null);
const composerBusy = signal<boolean>(false);
const importing = signal<boolean>(false);
/** Pastas da Biblioteca — carregadas 1x e usadas pelo dropdown de "Pasta". */
const libraryFolders = signal<LibraryFolderRow[]>([]);

async function loadLibraryFoldersOnce() {
  if (libraryFolders.value.length > 0) return;
  try {
    const r = await api.listLibraryFolders();
    libraryFolders.value = r.folders;
  } catch {
    /* silencioso — dropdown fica com só "Sem categoria" */
  }
}

async function loadAll() {
  listState.value = { ...listState.value, phase: "loading", errMsg: null };
  try {
    const { themes } = await api.listEssayThemes();
    listState.value = { phase: "ready", themes, errMsg: null };
  } catch (e) {
    listState.value = {
      phase: "error",
      themes: [],
      errMsg: e instanceof ApiError ? e.message : friendlyError(e)
    };
  }
}

function emptyTheme(): EssayThemeFull {
  return {
    id: "",
    slug: "",
    title: "",
    displayName: "",
    banca: "manual",
    year: null,
    phase: null,
    edition: null,
    applicationDate: null,
    examEditionKey: null,
    category: "geral",
    difficulty: "media",
    visibility: "private",
    status: "draft",
    originalSourceUrl: null,
    libraryFolderId: null,
    statement: "",
    officialInstructions: "",
    directives: [],
    fragments: [],
    createdAt: "",
    updatedAt: ""
  };
}

async function openComposerNew() {
  void loadLibraryFoldersOnce();
  composer.value = emptyTheme();
}

async function openComposerEdit(id: string) {
  composerBusy.value = true;
  void loadLibraryFoldersOnce();
  try {
    const full = await api.getEssayTheme(id);
    composer.value = full;
  } catch (e) {
    void openAlert({ title: "Erro", message: e instanceof ApiError ? e.message : friendlyError(e) });
  } finally {
    composerBusy.value = false;
  }
}

function closeComposer() {
  composer.value = null;
}

async function saveTheme() {
  const t = composer.value;
  if (!t) return;
  if (!t.title.trim()) {
    void openAlert({ title: "Validação", message: "Título é obrigatório." });
    return;
  }
  composerBusy.value = true;
  try {
    const input: EssayThemeUpsertInput = {
      id: t.id || undefined,
      slug: t.slug || undefined,
      title: t.title,
      displayName: t.displayName,
      banca: t.banca,
      year: t.year,
      phase: t.phase,
      edition: t.edition,
      applicationDate: t.applicationDate,
      examEditionKey: t.examEditionKey,
      category: t.category,
      difficulty: t.difficulty,
      visibility: t.visibility,
      status: t.status,
      statement: t.statement,
      officialInstructions: t.officialInstructions,
      directives: t.directives,
      originalSourceUrl: t.originalSourceUrl,
      libraryFolderId: t.libraryFolderId,
      fragments: t.fragments
    };
    await api.upsertEssayTheme(input);
    composer.value = null;
    await loadAll();
  } catch (e) {
    void openAlert({ title: "Erro", message: e instanceof ApiError ? e.message : friendlyError(e) });
  } finally {
    composerBusy.value = false;
  }
}

async function deleteTheme(id: string, displayName: string) {
  const ok = await openConfirm({
    title: "Excluir tema",
    message: `Remover "${displayName}" da Biblioteca de Redação? Esta ação não pode ser desfeita.`,
    danger: true
  });
  if (!ok) return;
  try {
    await api.deleteEssayTheme(id);
    await loadAll();
  } catch (e) {
    void openAlert({ title: "Erro", message: e instanceof ApiError ? e.message : friendlyError(e) });
  }
}

async function importFromJson() {
  const raw = prompt(
    "Cole o JSON do tema (shape EssayThemeUpsertInput).\nÚtil pra subir os 5 temas do draft."
  );
  if (!raw) return;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    void openAlert({ title: "Erro", message: "JSON inválido." });
    return;
  }
  importing.value = true;
  try {
    await api.ingestEssayThemeJson(parsed as EssayThemeUpsertInput);
    await loadAll();
  } catch (e) {
    void openAlert({ title: "Erro", message: e instanceof ApiError ? e.message : friendlyError(e) });
  } finally {
    importing.value = false;
  }
}

function updateComposer(patch: Partial<EssayThemeFull>) {
  if (!composer.value) return;
  composer.value = { ...composer.value, ...patch };
}

function updateFragment(index: number, patch: Partial<EssayThemeFragment>) {
  if (!composer.value) return;
  const frags = composer.value.fragments.slice();
  frags[index] = { ...frags[index], ...patch };
  composer.value = { ...composer.value, fragments: frags };
}

function addFragment() {
  if (!composer.value) return;
  const frags = composer.value.fragments.slice();
  frags.push({
    label: `Texto ${frags.length + 1}`,
    sourceOverall: "",
    note: "",
    position: frags.length,
    items: [
      { kind: "text", content: "", imageDataUrl: null, tableData: null, source: "", sourceKind: "real", position: 0 }
    ]
  });
  composer.value = { ...composer.value, fragments: frags };
}

function removeFragment(index: number) {
  if (!composer.value) return;
  const frags = composer.value.fragments.slice();
  frags.splice(index, 1);
  for (let i = 0; i < frags.length; i++) frags[i].position = i;
  composer.value = { ...composer.value, fragments: frags };
}

function updateItem(fragIndex: number, itemIndex: number, patch: Partial<EssayThemeItem>) {
  if (!composer.value) return;
  const frags = composer.value.fragments.slice();
  const items = frags[fragIndex].items.slice();
  items[itemIndex] = { ...items[itemIndex], ...patch };
  frags[fragIndex] = { ...frags[fragIndex], items };
  composer.value = { ...composer.value, fragments: frags };
}

function addItem(fragIndex: number, kind: "text" | "image" | "table") {
  if (!composer.value) return;
  const frags = composer.value.fragments.slice();
  const items = frags[fragIndex].items.slice();
  items.push({
    kind,
    content: "",
    imageDataUrl: null,
    tableData: kind === "table" ? { headers: [], rows: [] } : null,
    source: "",
    sourceKind: "real",
    position: items.length
  });
  frags[fragIndex] = { ...frags[fragIndex], items };
  composer.value = { ...composer.value, fragments: frags };
}

function removeItem(fragIndex: number, itemIndex: number) {
  if (!composer.value) return;
  const frags = composer.value.fragments.slice();
  const items = frags[fragIndex].items.slice();
  items.splice(itemIndex, 1);
  for (let i = 0; i < items.length; i++) items[i].position = i;
  frags[fragIndex] = { ...frags[fragIndex], items };
  composer.value = { ...composer.value, fragments: frags };
}

async function handleImagePick(fragIndex: number, itemIndex: number, file: File) {
  if (file.size > 2 * 1024 * 1024) {
    void openAlert({
      title: "Imagem grande",
      message: "Use imagens até 2MB no MVP (dataURL inline). R2 entra em fase futura."
    });
    return;
  }
  const reader = new FileReader();
  reader.onload = () => {
    const dataUrl = typeof reader.result === "string" ? reader.result : null;
    if (dataUrl) updateItem(fragIndex, itemIndex, { imageDataUrl: dataUrl });
  };
  reader.readAsDataURL(file);
}

export function EssayThemesView() {
  useEffect(() => {
    void loadAll();
  }, []);

  const state = listState.value;
  const c = composer.value;
  const busy = composerBusy.value;
  const imp = importing.value;

  return (
    <div class="p-6 lg:p-8 max-w-7xl mx-auto">
      <header class="flex items-center justify-between gap-4 mb-6">
        <div>
          <h1 class="text-2xl font-bold flex items-center gap-2">
            <PenSquare size={24} class="text-violet-500" />
            Biblioteca de Temas de Redação
          </h1>
          <p class="text-sm text-muted-foreground mt-1">
            Temas reutilizáveis (FUVEST, ENEM, UNICAMP, etc.) que o aluno pode aplicar no Modo Estudo de Redação.
          </p>
        </div>
        <div class="flex items-center gap-2">
          <button
            type="button"
            class="px-3 py-2 rounded-lg border text-sm hover:bg-muted transition-colors flex items-center gap-2"
            onClick={() => void loadAll()}
          >
            <RefreshCw size={14} />
            Recarregar
          </button>
          <button
            type="button"
            class="px-3 py-2 rounded-lg border text-sm hover:bg-muted transition-colors flex items-center gap-2"
            onClick={() => void importFromJson()}
            disabled={imp}
          >
            {imp ? <Loader2 size={14} class="animate-spin" /> : <Upload size={14} />}
            Importar JSON
          </button>
          <button
            type="button"
            class="px-3 py-2 rounded-lg bg-violet-600 text-white text-sm hover:bg-violet-700 transition-colors flex items-center gap-2"
            onClick={() => void openComposerNew()}
          >
            <Plus size={14} />
            Novo tema
          </button>
        </div>
      </header>

      {state.phase === "loading" && (
        <div class="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} class="h-20 w-full" />
          ))}
        </div>
      )}

      {state.phase === "error" && (
        <div class="border border-destructive/40 bg-destructive/10 text-destructive rounded-lg p-4 flex items-start gap-2">
          <AlertCircle size={18} class="mt-0.5" />
          <div class="flex-1">
            <p class="font-medium">Erro carregando temas</p>
            <p class="text-sm">{state.errMsg}</p>
          </div>
        </div>
      )}

      {state.phase === "ready" && state.themes.length === 0 && (
        <div class="border-2 border-dashed rounded-lg p-12 text-center">
          <FilePlus2 size={48} class="mx-auto text-muted-foreground/50 mb-3" />
          <p class="font-medium">Nenhum tema cadastrado ainda</p>
          <p class="text-sm text-muted-foreground mt-1">
            Crie manualmente ou importe um JSON pré-formatado.
          </p>
        </div>
      )}

      {state.phase === "ready" && state.themes.length > 0 && (
        <div class="border rounded-lg overflow-hidden">
          <table class="w-full text-sm">
            <thead class="bg-muted/50 border-b">
              <tr>
                <th class="text-left p-3 font-medium">Tema</th>
                <th class="text-left p-3 font-medium">Banca</th>
                <th class="text-left p-3 font-medium">Data</th>
                <th class="text-left p-3 font-medium">Visibilidade</th>
                <th class="text-left p-3 font-medium">Status</th>
                <th class="text-right p-3 font-medium">Ações</th>
              </tr>
            </thead>
            <tbody>
              {state.themes.map((t) => (
                <tr key={t.id} class="border-t hover:bg-muted/30">
                  <td class="p-3">
                    <div class="font-medium">{t.displayName || t.title}</div>
                    <div class="text-xs text-muted-foreground">{t.title}</div>
                  </td>
                  <td class="p-3 text-xs">
                    <span class="px-2 py-0.5 rounded bg-muted">{t.banca}</span>
                  </td>
                  <td class="p-3 text-xs">
                    {t.applicationDate
                      ? new Date(t.applicationDate).toLocaleDateString("pt-BR")
                      : t.year ?? "—"}
                  </td>
                  <td class="p-3 text-xs">
                    <span
                      class={`px-2 py-0.5 rounded ${
                        t.visibility === "public"
                          ? "bg-green-500/15 text-green-700 dark:text-green-300"
                          : t.visibility === "unlisted"
                            ? "bg-amber-500/15 text-amber-700 dark:text-amber-300"
                            : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {t.visibility}
                    </span>
                  </td>
                  <td class="p-3 text-xs">
                    <span
                      class={`px-2 py-0.5 rounded ${
                        t.status === "published"
                          ? "bg-violet-500/15 text-violet-700 dark:text-violet-300"
                          : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {t.status}
                    </span>
                  </td>
                  <td class="p-3 text-right">
                    <button
                      type="button"
                      class="p-1.5 rounded hover:bg-muted transition-colors mr-1"
                      title="Editar"
                      onClick={() => void openComposerEdit(t.id)}
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      type="button"
                      class="p-1.5 rounded hover:bg-destructive/15 text-destructive transition-colors"
                      title="Excluir"
                      onClick={() => void deleteTheme(t.id, t.displayName || t.title)}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {c && (
        <div class="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-6">
          <div class="bg-card border rounded-xl shadow-2xl w-full max-w-4xl my-6">
            <header class="border-b p-4 flex items-center justify-between">
              <h2 class="text-lg font-semibold">
                {c.id ? "Editar tema" : "Novo tema"}
              </h2>
              <button type="button" onClick={closeComposer} class="p-1 rounded hover:bg-muted">
                <X size={18} />
              </button>
            </header>

            <div class="p-4 space-y-6 max-h-[80vh] overflow-y-auto">
              {/* Metadados */}
              <section class="space-y-3">
                <h3 class="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                  Metadados
                </h3>
                <div class="grid grid-cols-2 gap-3">
                  <label class="block">
                    <span class="text-xs text-muted-foreground">Título *</span>
                    <input
                      type="text"
                      class="w-full mt-1 px-2 py-1.5 rounded border bg-background text-sm"
                      value={c.title}
                      onInput={(e) => updateComposer({ title: (e.target as HTMLInputElement).value })}
                    />
                  </label>
                  <label class="block">
                    <span class="text-xs text-muted-foreground">Display name (exibição)</span>
                    <input
                      type="text"
                      class="w-full mt-1 px-2 py-1.5 rounded border bg-background text-sm"
                      placeholder="FUVEST 2026 · 2ª fase · Dia 1"
                      value={c.displayName}
                      onInput={(e) => updateComposer({ displayName: (e.target as HTMLInputElement).value })}
                    />
                  </label>
                  <label class="block">
                    <span class="text-xs text-muted-foreground">Slug</span>
                    <input
                      type="text"
                      class="w-full mt-1 px-2 py-1.5 rounded border bg-background text-sm font-mono"
                      placeholder="auto se vazio"
                      value={c.slug}
                      onInput={(e) => updateComposer({ slug: (e.target as HTMLInputElement).value })}
                    />
                  </label>
                  <label class="block">
                    <span class="text-xs text-muted-foreground">Banca</span>
                    <input
                      type="text"
                      class="w-full mt-1 px-2 py-1.5 rounded border bg-background text-sm"
                      value={c.banca}
                      onInput={(e) => updateComposer({ banca: (e.target as HTMLInputElement).value })}
                    />
                  </label>
                  <label class="block">
                    <span class="text-xs text-muted-foreground">Ano</span>
                    <input
                      type="number"
                      class="w-full mt-1 px-2 py-1.5 rounded border bg-background text-sm"
                      value={c.year ?? ""}
                      onInput={(e) => {
                        const v = (e.target as HTMLInputElement).value;
                        updateComposer({ year: v ? parseInt(v, 10) : null });
                      }}
                    />
                  </label>
                  <label class="block">
                    <span class="text-xs text-muted-foreground">Fase</span>
                    <input
                      type="text"
                      class="w-full mt-1 px-2 py-1.5 rounded border bg-background text-sm"
                      placeholder="2a-fase, regular, ppl, unica…"
                      value={c.phase ?? ""}
                      onInput={(e) => updateComposer({ phase: (e.target as HTMLInputElement).value || null })}
                    />
                  </label>
                  <label class="block">
                    <span class="text-xs text-muted-foreground">Edition (sub-edição)</span>
                    <input
                      type="text"
                      class="w-full mt-1 px-2 py-1.5 rounded border bg-background text-sm"
                      placeholder="Dia 1 — Caderno Amarelo / Proposta 1"
                      value={c.edition ?? ""}
                      onInput={(e) => updateComposer({ edition: (e.target as HTMLInputElement).value || null })}
                    />
                  </label>
                  <label class="block">
                    <span class="text-xs text-muted-foreground">Data de aplicação</span>
                    <input
                      type="date"
                      class="w-full mt-1 px-2 py-1.5 rounded border bg-background text-sm"
                      value={c.applicationDate ? c.applicationDate.slice(0, 10) : ""}
                      onInput={(e) => updateComposer({ applicationDate: (e.target as HTMLInputElement).value || null })}
                    />
                  </label>
                  <label class="block">
                    <span class="text-xs text-muted-foreground">Exam edition key (agrupa propostas)</span>
                    <input
                      type="text"
                      class="w-full mt-1 px-2 py-1.5 rounded border bg-background text-sm font-mono"
                      placeholder="fuvest-2026-2a-fase-dia1"
                      value={c.examEditionKey ?? ""}
                      onInput={(e) => updateComposer({ examEditionKey: (e.target as HTMLInputElement).value || null })}
                    />
                  </label>
                  <label class="block">
                    <span class="text-xs text-muted-foreground">URL do PDF oficial</span>
                    <input
                      type="url"
                      class="w-full mt-1 px-2 py-1.5 rounded border bg-background text-sm"
                      value={c.originalSourceUrl ?? ""}
                      onInput={(e) => updateComposer({ originalSourceUrl: (e.target as HTMLInputElement).value || null })}
                    />
                  </label>
                  <label class="block">
                    <span class="text-xs text-muted-foreground">Visibilidade</span>
                    <select
                      class="w-full mt-1 px-2 py-1.5 rounded border bg-background text-sm"
                      value={c.visibility}
                      onChange={(e) => updateComposer({ visibility: (e.target as HTMLSelectElement).value as any })}
                    >
                      <option value="private">private</option>
                      <option value="unlisted">unlisted</option>
                      <option value="public">public</option>
                    </select>
                  </label>
                  <label class="block">
                    <span class="text-xs text-muted-foreground">Status</span>
                    <select
                      class="w-full mt-1 px-2 py-1.5 rounded border bg-background text-sm"
                      value={c.status}
                      onChange={(e) => updateComposer({ status: (e.target as HTMLSelectElement).value as any })}
                    >
                      <option value="draft">draft</option>
                      <option value="published">published</option>
                      <option value="archived">archived</option>
                    </select>
                  </label>
                  <label class="block">
                    <span class="text-xs text-muted-foreground">
                      Pasta na Biblioteca <span class="text-[10px] opacity-70">(só relevante se visibility=público)</span>
                    </span>
                    <select
                      class="w-full mt-1 px-2 py-1.5 rounded border bg-background text-sm"
                      value={c.libraryFolderId ?? ""}
                      onChange={(e) =>
                        updateComposer({
                          libraryFolderId: (e.target as HTMLSelectElement).value || null
                        })
                      }
                    >
                      <option value="">Sem categoria</option>
                      {libraryFolders.value.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.name}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </section>

              {/* Proposta */}
              <section class="space-y-2">
                <h3 class="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                  Proposta (statement, markdown)
                </h3>
                <textarea
                  class="w-full px-3 py-2 rounded border bg-background text-sm font-mono"
                  rows={6}
                  value={c.statement}
                  onInput={(e) => updateComposer({ statement: (e.target as HTMLTextAreaElement).value })}
                />
              </section>

              {/* Instruções oficiais */}
              <section class="space-y-2">
                <h3 class="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                  Instruções oficiais (do caderno, markdown)
                </h3>
                <textarea
                  class="w-full px-3 py-2 rounded border bg-background text-sm font-mono"
                  rows={6}
                  value={c.officialInstructions}
                  onInput={(e) => updateComposer({ officialInstructions: (e.target as HTMLTextAreaElement).value })}
                />
              </section>

              {/* Direcionadores */}
              <section class="space-y-2">
                <h3 class="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                  Direcionadores (1 por linha, markdown OK)
                </h3>
                <textarea
                  class="w-full px-3 py-2 rounded border bg-background text-sm font-mono"
                  rows={4}
                  value={c.directives.join("\n")}
                  onInput={(e) => {
                    const lines = (e.target as HTMLTextAreaElement).value
                      .split("\n")
                      .map((l) => l.trim())
                      .filter((l) => l.length > 0);
                    updateComposer({ directives: lines });
                  }}
                />
              </section>

              {/* Fragments */}
              <section class="space-y-3">
                <div class="flex items-center justify-between">
                  <h3 class="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                    Fragmentos ({c.fragments.length})
                  </h3>
                  <button
                    type="button"
                    class="px-2 py-1 rounded border text-xs hover:bg-muted flex items-center gap-1"
                    onClick={addFragment}
                  >
                    <Plus size={12} />
                    Adicionar fragmento
                  </button>
                </div>

                {c.fragments.map((frag, fi) => (
                  <div key={fi} class="border rounded-lg p-3 space-y-3 bg-muted/20">
                    <div class="flex items-start gap-2">
                      <div class="flex-1 grid grid-cols-2 gap-2">
                        <input
                          type="text"
                          class="px-2 py-1 rounded border bg-background text-sm"
                          placeholder="Label (ex: Texto I)"
                          value={frag.label}
                          onInput={(e) => updateFragment(fi, { label: (e.target as HTMLInputElement).value })}
                        />
                        <input
                          type="text"
                          class="px-2 py-1 rounded border bg-background text-sm"
                          placeholder="Source overall (ex: Folha de SP, 03/04/2025)"
                          value={frag.sourceOverall}
                          onInput={(e) => updateFragment(fi, { sourceOverall: (e.target as HTMLInputElement).value })}
                        />
                      </div>
                      <button
                        type="button"
                        class="p-1.5 rounded hover:bg-destructive/15 text-destructive"
                        title="Remover fragmento"
                        onClick={() => removeFragment(fi)}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>

                    {frag.items.map((it, ii) => (
                      <div key={ii} class="border rounded p-2 space-y-2 bg-card">
                        <div class="flex items-center gap-2">
                          <span
                            class={`px-2 py-0.5 rounded text-xs ${
                              it.kind === "image"
                                ? "bg-blue-500/15 text-blue-700 dark:text-blue-300"
                                : it.kind === "table"
                                  ? "bg-purple-500/15 text-purple-700 dark:text-purple-300"
                                  : "bg-muted"
                            }`}
                          >
                            {it.kind === "image" ? <ImageIcon size={10} class="inline" /> : it.kind === "table" ? <Table2 size={10} class="inline" /> : "T"} {it.kind}
                          </span>
                          <select
                            class="px-2 py-0.5 rounded border bg-background text-xs"
                            value={it.sourceKind}
                            onChange={(e) => updateItem(fi, ii, { sourceKind: (e.target as HTMLSelectElement).value as any })}
                          >
                            <option value="real">real</option>
                            <option value="ai_generated">ai_generated</option>
                            <option value="unknown">unknown</option>
                          </select>
                          <input
                            type="text"
                            class="flex-1 px-2 py-0.5 rounded border bg-background text-xs"
                            placeholder="Source específico do item (opcional)"
                            value={it.source}
                            onInput={(e) => updateItem(fi, ii, { source: (e.target as HTMLInputElement).value })}
                          />
                          <button
                            type="button"
                            class="p-1 rounded hover:bg-destructive/15 text-destructive"
                            onClick={() => removeItem(fi, ii)}
                          >
                            <X size={12} />
                          </button>
                        </div>

                        {it.kind === "text" && (
                          <textarea
                            class="w-full px-2 py-1 rounded border bg-background text-xs font-mono"
                            rows={4}
                            placeholder="Conteúdo do texto (markdown)"
                            value={it.content}
                            onInput={(e) => updateItem(fi, ii, { content: (e.target as HTMLTextAreaElement).value })}
                          />
                        )}

                        {it.kind === "image" && (
                          <div class="space-y-1">
                            <input
                              type="file"
                              accept="image/*"
                              class="text-xs"
                              onChange={(e) => {
                                const f = (e.target as HTMLInputElement).files?.[0];
                                if (f) void handleImagePick(fi, ii, f);
                              }}
                            />
                            {it.imageDataUrl && (
                              <img src={it.imageDataUrl} class="max-h-32 rounded border" />
                            )}
                            <input
                              type="text"
                              class="w-full px-2 py-1 rounded border bg-background text-xs"
                              placeholder="Legenda (opcional)"
                              value={it.content}
                              onInput={(e) => updateItem(fi, ii, { content: (e.target as HTMLInputElement).value })}
                            />
                          </div>
                        )}

                        {it.kind === "table" && (
                          <textarea
                            class="w-full px-2 py-1 rounded border bg-background text-xs font-mono"
                            rows={4}
                            placeholder='JSON: { "headers": [...], "rows": [[...]] }'
                            value={JSON.stringify(it.tableData ?? { headers: [], rows: [] }, null, 2)}
                            onInput={(e) => {
                              try {
                                const parsed = JSON.parse((e.target as HTMLTextAreaElement).value);
                                updateItem(fi, ii, { tableData: parsed });
                              } catch {
                                // não atualiza enquanto JSON inválido
                              }
                            }}
                          />
                        )}
                      </div>
                    ))}

                    <div class="flex gap-2">
                      <button
                        type="button"
                        class="px-2 py-1 rounded border text-xs hover:bg-muted flex items-center gap-1"
                        onClick={() => addItem(fi, "text")}
                      >
                        <Plus size={10} /> Texto
                      </button>
                      <button
                        type="button"
                        class="px-2 py-1 rounded border text-xs hover:bg-muted flex items-center gap-1"
                        onClick={() => addItem(fi, "image")}
                      >
                        <Plus size={10} /> Imagem
                      </button>
                      <button
                        type="button"
                        class="px-2 py-1 rounded border text-xs hover:bg-muted flex items-center gap-1"
                        onClick={() => addItem(fi, "table")}
                      >
                        <Plus size={10} /> Tabela
                      </button>
                    </div>
                  </div>
                ))}
              </section>
            </div>

            <footer class="border-t p-3 flex items-center justify-end gap-2 bg-muted/30">
              <button
                type="button"
                class="px-3 py-1.5 rounded border text-sm hover:bg-muted"
                onClick={closeComposer}
                disabled={busy}
              >
                Cancelar
              </button>
              <button
                type="button"
                class="px-3 py-1.5 rounded bg-violet-600 text-white text-sm hover:bg-violet-700 flex items-center gap-2"
                onClick={() => void saveTheme()}
                disabled={busy}
              >
                {busy ? <Loader2 size={14} class="animate-spin" /> : <Save size={14} />}
                Salvar
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}
